import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {Account} from "@/integrations/supabase/account.models.ts";
import {toDeleteBlockedError} from "@/lib/errors/deleteBlockedError.ts";

/**
 * Account Service manages account operations
 */
export class AccountService extends BaseService {

    /**
     * Create a new account
     * @param {AccountCreateRequest} request request to create account
     */
    async createAccount(request: AccountCreateRequest): Promise<Account> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('accounts')
                .insert(payload)
                .select()
                .single();

            if (error) {
                throw error;
            }
            return data;
        } catch (e) {
            await this.handleError(e);
            throw e
        }
    }

    /**
     * Update an account
     * @param {AccountUpdateRequest} request Account update request
     */
    async updateAccount(request: AccountUpdateRequest): Promise<Account> {
        // The balance only changes through transactions (issue #128), so it is never written here.
        const { balance: _balance, ...rest } = request;
        const payload = this.convertRequestToSnakeCase(rest);
        try {
            const { data, error } = await supabase
                .from('accounts')
                .update(payload)
                .eq('id', payload.id)
                .select()
                .single();
            if (error) {
                throw error;
            }
            return data;
        } catch (e) {
            await this.handleError(e);
            throw e
        }
    }

    /**
     * Delete an account
     * @param {string} id Account ID to delete
     * @param {string} userId User ID to delete account for
     */
    async deleteAccount(id: string, userId: string): Promise<void> {
        const { error } = await supabase
            .from('accounts')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);

        if (error) {
            const reported = toDeleteBlockedError(error,
                "This account has transactions, so it can't be deleted. Set its status to Inactive instead.");
            await this.handleError(reported as Error);
            throw reported;
        }
    }

    /**
     * Retrieves an account given its ID for a given user
     * @param {string} id Account ID to retrieve
     * @param {string} userId User ID to retrieve account for
     * @returns {Account} retrieved account
     */
    async fetchAccountById(id: string, userId: string) : Promise<Account> {
        this.validateUUID(id);
        const { data, error } = await supabase
            .from('accounts')
            .select()
            .eq('id', id)
            .eq('user_id', userId)
            .single();

        // TODO: handle and throw specific errors
        if (error) {
            await this.handleError(error);
            // Handle specific error cases
            if (error.code === 'PGRST116') {
                throw new Error('Account not found');
            }
            throw error
        }
        return data
    }

    /**
     * Fetch all accounts for the given user
     * @param {string} userId User ID to retrieve accounts for
     * @returns {Account[]} retrieved accounts
     */
    async fetchAllAccounts(userId: string) : Promise<Account[]> {
        try {
            const { data, error } = await supabase
                .from('accounts')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });
            if (error) {
                throw error;
            }
            return data
        } catch (e) {
            await this.handleError(e);
            throw e
        }
    }
}

export default new AccountService();
